import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

/**
 * Dashboard warning shown to admins whenever the most recent security scan
 * snapshot introduced findings that were not present before.
 */
export function SecurityNewFindingsBanner() {
  const [state, setState] = useState<{ newCount: number; at: string; ids: string[] } | null>(null);
  const notifiedAt = useRef<string | null>(null);

  const load = async () => {
    const [{ data }, { data: cfg }] = await Promise.all([
      supabase
        .from("security_scan_snapshots")
        .select("id, new_count, created_at, findings")
        .order("created_at", { ascending: false })
        .limit(1),
      supabase
        .from("security_alert_settings")
        .select("notify_web_push, enabled")
        .eq("id", true)
        .maybeSingle(),
    ]);
    const snap = (data ?? [])[0] as unknown as
      | { new_count: number; created_at: string; findings?: Array<{ internal_id: string }> }
      | undefined;

    if (!snap || !snap.new_count) return setState(null);
    setState({
      newCount: snap.new_count,
      at: snap.created_at,
      ids: (snap.findings ?? []).slice(0, 4).map((f) => f.internal_id),
    });

    // Optional web push / desktop alert for admins who opted in.
    const push = (cfg as { notify_web_push?: boolean; enabled?: boolean } | null) ?? null;
    if (
      push?.enabled &&
      push?.notify_web_push &&
      notifiedAt.current !== snap.created_at &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      notifiedAt.current = snap.created_at;
      try {
        new Notification(`${snap.new_count} new security finding(s)`, {
          body: (snap.findings ?? []).slice(0, 3).map((f) => f.internal_id).join(", "),
          tag: `security-scan-${snap.created_at}`,
        });
      } catch {
        // notifications unavailable — the banner still shows
      }
    }
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel(`admin-security-new-findings:${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "security_scan_snapshots" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (!state) return null;

  return (
    <div
      role="alert"
      className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 flex flex-wrap items-start justify-between gap-3"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-semibold text-destructive">
          <ShieldAlert className="size-4" />
          {state.newCount} new security finding{state.newCount === 1 ? "" : "s"} detected
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          From the scan on {new Date(state.at).toLocaleString()}.
          {state.ids.length > 0 && (
            <span className="font-mono"> {state.ids.join(", ")}</span>
          )}
        </p>
      </div>
      <div className="flex gap-2">
        <Button size="sm" asChild>
          <Link to="/admin/security-findings">Review findings</Link>
        </Button>
        <Button size="sm" variant="outline" asChild>
          <Link to="/admin/security-history">History</Link>
        </Button>
      </div>
    </div>
  );
}
